import ReportLead from '../shared/ReportLead';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Box, Card, CardContent, LinearProgress, Stack, Typography, useTheme } from '@mui/material';
import { AppVarsContext } from '../../context/AppVarsContext';
import {
    EmailGallerySection,
    EmailInsightsCard,
    EmailMetricGrid,
    EmailTableSection,
    formatEmailDate,
} from '../shared/emailReportShared';
import {
    getPlainSurfaceCardSx,
    getReportBackdropSx,
    getReportCanvasSx,
} from '../shared/reportSurfaceStyles';

const toNumber = (value) => {
    const num = Number(value);
    return Number.isFinite(num) ? num : 0;
};

const formatNumber = (value) => toNumber(value).toLocaleString();
const formatPercent = (value, digits = 1) => `${toNumber(value).toFixed(digits)}%`;

const readMetric = (row, keys, fallback = 0) => {
    if (!row) return fallback;
    for (const key of keys) {
        if (row && typeof row[key] !== 'undefined' && row[key] !== null) return row[key];
        if (row?.stats && typeof row.stats[key] !== 'undefined' && row.stats[key] !== null) return row.stats[key];
        if (row?.metrics && typeof row.metrics[key] !== 'undefined' && row.metrics[key] !== null) return row.metrics[key];
    }
    return fallback;
};

const normalizeRows = (payload, fallbackKey = 'rows') => {
    if (!payload) return [];
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.[fallbackKey])) return payload[fallbackKey];
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.results)) return payload.results;
    return [];
};

const readPreviewUrl = (row) => (
    row?.web_version_url
    || row?.webVersionUrl
    || row?.preview_url
    || row?.previewUrl
    || row?.archive_url
    || row?.archiveUrl
    || row?.url
    || row?.email_url
    || row?.raw?.web_version_url
    || row?.raw?.webVersionUrl
    || row?.raw?.preview_url
    || row?.raw?.previewUrl
    || row?.raw?.archive_url
    || row?.raw?.archiveUrl
    || row?.raw?.url
    || row?.raw?.email_url
    || ''
);

const readDirectImageUrl = (row) => (
    row?.thumbnail_url
    || row?.thumbnailUrl
    || row?.preview_thumbnail_url
    || row?.previewThumbnailUrl
    || row?.image_url
    || row?.imageUrl
    || row?.raw?.thumbnail_url
    || row?.raw?.thumbnailUrl
    || row?.raw?.preview_thumbnail_url
    || row?.raw?.previewThumbnailUrl
    || row?.raw?.image_url
    || row?.raw?.imageUrl
    || ''
);

const HubSpotReport = (props) => {
    const cid = sessionStorage.getItem('rid');
    const { appVars, appLoading } = useContext(AppVarsContext);
    const theme = useTheme();
    const setPageLoading = props.setLoading;

    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState('');
    const [emailsRaw, setEmailsRaw] = useState([]);
    const [formsRaw, setFormsRaw] = useState([]);

    const fetchData = useCallback(async () => {
        if (!appVars?.api_url || !cid) return;
        const sdate = sessionStorage.getItem('sDate');
        const edate = sessionStorage.getItem('eDate');
        if (!sdate || !edate) return;

        setLoading(true);
        setErr('');
        try {
            const [emailsRes, formsRes] = await Promise.all([
                fetch(`${appVars.api_url}/hubspot/email-stats`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ cid, sdate, edate }),
                }),
                fetch(`${appVars.api_url}/hubspot/form-signups`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ cid, sdate, edate }),
                }),
            ]);

            const emailsJson = await emailsRes.json();
            const formsJson = await formsRes.json();

            if (!emailsRes.ok) throw new Error(emailsJson?.error || `Emails request failed (${emailsRes.status})`);
            if (!formsRes.ok) throw new Error(formsJson?.error || `Forms request failed (${formsRes.status})`);

            setEmailsRaw(normalizeRows(emailsJson));
            setFormsRaw(normalizeRows(formsJson));
            setPageLoading?.(false);
        } catch (error) {
            setErr(error?.message || String(error));
            setEmailsRaw([]);
            setFormsRaw([]);
        } finally {
            setLoading(false);
            setPageLoading?.(false);
        }
    }, [appVars?.api_url, cid, setPageLoading]);

    useEffect(() => {
        if (!appLoading) {
            fetchData();
        }
    }, [appLoading, fetchData]);

    useEffect(() => {
        const handleStorageChange = (event) => {
            fetchData();
        };
        window.addEventListener('dc', handleStorageChange);
        return () => window.removeEventListener('dc', handleStorageChange);
    }, [fetchData]);

    const emailRows = useMemo(() => emailsRaw.map((row, index) => {
        const name = row?.name || row?.subject || row?.email_name || row?.campaignName || `Email ${index + 1}`;
        const sent = toNumber(readMetric(row, ['sent', 'sends', 'send', 'num_sent']));
        const delivered = toNumber(readMetric(row, ['delivered', 'deliveries', 'delivered_count'])) || sent;
        const opens = toNumber(readMetric(row, ['opens', 'open', 'open_count']));
        const clicks = toNumber(readMetric(row, ['clicks', 'click', 'click_count']));
        const unsubs = toNumber(readMetric(row, ['unsubscribes', 'unsubs', 'unsubscribed']));
        const openRate = delivered ? (opens / delivered) * 100 : 0;
        const clickRate = delivered ? (clicks / delivered) * 100 : 0;
        const ctor = opens ? (clicks / opens) * 100 : 0;
        return { id: row?.id || row?.emailId || `hubspot-email-${index}`, name, sent, delivered, opens, clicks, unsubs, openRate, clickRate, ctor, sendDate: row?.sendDate || row?.send_date || row?.createdAt || row?.created_at, raw: row };
    }), [emailsRaw]);

    const formRows = useMemo(() => formsRaw.map((row, index) => {
        const name = row?.name || row?.form_name || row?.title || `Form ${index + 1}`;
        const views = toNumber(readMetric(row, ['views', 'view', 'impressions']));
        const submissions = toNumber(readMetric(row, ['submissions', 'submits', 'submission_count', 'conversions']));
        const conversionRate = views ? (submissions / views) * 100 : 0;
        return { id: row?.id || `hubspot-form-${index}`, name, views, submissions, conversionRate };
    }), [formsRaw]);

    const emailTotals = useMemo(() => emailRows.reduce((acc, row) => {
        acc.sent += row.sent;
        acc.delivered += row.delivered;
        acc.opens += row.opens;
        acc.clicks += row.clicks;
        acc.unsubs += row.unsubs;
        return acc;
    }, { sent: 0, delivered: 0, opens: 0, clicks: 0, unsubs: 0 }), [emailRows]);

    const formTotals = useMemo(() => formRows.reduce((acc, row) => {
        acc.views += row.views;
        acc.submissions += row.submissions;
        return acc;
    }, { views: 0, submissions: 0 }), [formRows]);

    const emailRateSummary = useMemo(() => {
        const openRate = emailTotals.delivered ? (emailTotals.opens / emailTotals.delivered) * 100 : 0;
        const clickRate = emailTotals.delivered ? (emailTotals.clicks / emailTotals.delivered) * 100 : 0;
        const ctor = emailTotals.opens ? (emailTotals.clicks / emailTotals.opens) * 100 : 0;
        return { openRate, clickRate, ctor };
    }, [emailTotals]);

    const formConversionRate = useMemo(() => (
        formTotals.views ? (formTotals.submissions / formTotals.views) * 100 : 0
    ), [formTotals]);

    const rankedEmails = useMemo(() => ({
        topOpen: [...emailRows].sort((a, b) => b.openRate - a.openRate)[0] || null,
        topClick: [...emailRows].sort((a, b) => b.clickRate - a.clickRate)[0] || null,
        topClicks: [...emailRows].sort((a, b) => b.clicks - a.clicks)[0] || null,
    }), [emailRows]);

    const insightItems = useMemo(() => {
        const items = [];
        if (rankedEmails.topOpen) {
            items.push({
                tone: 'success',
                label: 'Top Open Rate',
                text: `${rankedEmails.topOpen.name} produced the strongest open rate at ${formatPercent(rankedEmails.topOpen.openRate)}.`,
            });
        }
        if (rankedEmails.topClick) {
            items.push({
                tone: 'success',
                label: 'Top Click Rate',
                text: `${rankedEmails.topClick.name} led HubSpot click rate at ${formatPercent(rankedEmails.topClick.clickRate)}.`,
            });
        }
        if (emailRateSummary.clickRate < 1) {
            items.push({
                tone: 'warning',
                label: 'Click Efficiency',
                text: `Overall click rate is only ${formatPercent(emailRateSummary.clickRate)}. HubSpot emails are being delivered, but not enough traffic is coming through.`,
            });
        }
        if (formConversionRate > 0) {
            items.push({
                tone: 'default',
                label: 'Form Follow-Through',
                text: `HubSpot forms converted ${formatPercent(formConversionRate)} of their views in the same window, which helps explain post-email capture strength.`,
            });
        }
        return items.slice(0, 4);
    }, [emailRateSummary.clickRate, formConversionRate, rankedEmails.topClick, rankedEmails.topOpen]);

    const metricItems = useMemo(() => ([
        { label: 'Emails', value: formatNumber(emailRows.length), helper: 'In selected range' },
        { label: 'Sent', value: formatNumber(emailTotals.sent), helper: 'HubSpot email sends' },
        { label: 'Delivered', value: formatNumber(emailTotals.delivered), helper: 'Successful deliveries' },
        { label: 'Opens', value: formatNumber(emailTotals.opens), helper: `Open Rate ${formatPercent(emailRateSummary.openRate)}` },
        { label: 'Clicks', value: formatNumber(emailTotals.clicks), helper: `Click Rate ${formatPercent(emailRateSummary.clickRate)}` },
        { label: 'CTOR', value: formatPercent(emailRateSummary.ctor), helper: 'Clicks from opens' },
        { label: 'Unsubs', value: formatNumber(emailTotals.unsubs), helper: 'Email unsubscribes' },
        { label: 'Form Submits', value: formatNumber(formTotals.submissions), helper: `Form Conv ${formatPercent(formConversionRate)}` },
    ]), [emailRateSummary, emailRows.length, emailTotals, formConversionRate, formTotals.submissions]);

    const galleryRows = useMemo(() => emailRows.map((row) => {
        const previewHref = readPreviewUrl(row);
        return {
            id: row.id,
            name: row.name,
            sendDate: row.sendDate,
            previewText: row.raw?.preview_text || row.raw?.content_preview || row.raw?.description || '',
            previewHref,
            captureUrl: previewHref,
            directImageUrl: readDirectImageUrl(row),
            placeholder: 'HS',
            metricItems: [
                { label: 'Sent', value: formatNumber(row.sent) },
                { label: 'Opens', value: `${formatNumber(row.opens)} · ${formatPercent(row.openRate)}` },
                { label: 'Clicks', value: `${formatNumber(row.clicks)} · ${formatPercent(row.clickRate)}` },
                { label: 'CTOR', value: formatPercent(row.ctor) },
            ],
        };
    }), [emailRows]);

    const sortedEmailRows = useMemo(() => [...emailRows].sort((a, b) => {
        const aDate = new Date(a.sendDate).getTime() || 0;
        const bDate = new Date(b.sendDate).getTime() || 0;
        return bDate - aDate;
    }), [emailRows]);

    return (
        <Box sx={{ p: { xs: 1, md: 1 } }}>
            <Card><CardContent><ReportLead eyebrow="HubSpot" title="From delivery to engagement" description="Review email delivery, engagement, and the forms generating response." /></CardContent></Card>
            {loading && <LinearProgress aria-label="Loading HubSpot report" />}

            <Box sx={getReportCanvasSx(theme)}>
                <Box sx={getReportBackdropSx(theme)} />

                {err ? (
                    <Card sx={getPlainSurfaceCardSx(theme)}>
                        <CardContent>
                            <Typography variant="body2" color="error.main">{err}</Typography>
                        </CardContent>
                    </Card>
                ) : null}

                <EmailMetricGrid items={metricItems} />

                <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', xl: '1.1fr 0.9fr' } }}>
                    <Card sx={getPlainSurfaceCardSx(theme)}>
                        <CardContent>
                            <Stack spacing={1.5}>
                                <Box>
                                    <Typography variant="h6" sx={{ fontWeight: 800 }}>
                                        Email Engagement Funnel
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.45 }}>
                                        Use the same delivery-to-click readout as the other email reports.
                                    </Typography>
                                </Box>

                                {[
                                    { label: 'Delivered', value: emailTotals.delivered, denom: emailTotals.sent, color: theme.palette.primary.main },
                                    { label: 'Opened', value: emailTotals.opens, denom: emailTotals.delivered, color: theme.palette.secondary.main },
                                    { label: 'Clicked', value: emailTotals.clicks, denom: emailTotals.delivered, color: theme.palette.success.main },
                                ].map((item) => {
                                    const percent = item.denom ? (item.value / item.denom) * 100 : 0;
                                    return (
                                        <Box key={item.label} sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                                                <Typography variant="body2">{item.label}</Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {formatNumber(item.value)} · {formatPercent(percent)}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ height: 8, borderRadius: 999, overflow: 'hidden', bgcolor: theme.palette.action.hover }}>
                                                <Box sx={{ width: `${Math.min(percent, 100)}%`, height: '100%', background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)` }} />
                                            </Box>
                                        </Box>
                                    );
                                })}
                            </Stack>
                        </CardContent>
                    </Card>

                    <EmailInsightsCard
                        title="What Needs Attention"
                        subtitle="HubSpot email insights, with form performance kept as a secondary reference."
                        items={insightItems}
                    />
                </Box>

                <EmailGallerySection
                    providerLabel="HubSpot"
                    title="Emails In Range"
                    subtitle="Email cards with thumbnails whenever HubSpot provides a preview or web-version URL."
                    rows={galleryRows}
                />

                <EmailTableSection
                    title="Email Performance"
                    subtitle="Consistent delivery and engagement detail for each HubSpot email."
                    rows={sortedEmailRows}
                    emptyMessage="No HubSpot email activity available for the selected period."
                    columns={[
                        {
                            key: 'name',
                            label: 'Email',
                            render: (row) => (
                                <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                        {row.name}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {formatEmailDate(row.sendDate)}
                                    </Typography>
                                </Box>
                            ),
                        },
                        { key: 'sent', label: 'Sent', align: 'right', render: (row) => formatNumber(row.sent) },
                        { key: 'delivered', label: 'Delivered', align: 'right', render: (row) => formatNumber(row.delivered) },
                        { key: 'opens', label: 'Opens', align: 'right', render: (row) => formatNumber(row.opens) },
                        { key: 'clicks', label: 'Clicks', align: 'right', render: (row) => formatNumber(row.clicks) },
                        { key: 'openRate', label: 'Open Rate', align: 'right', render: (row) => formatPercent(row.openRate) },
                        { key: 'clickRate', label: 'Click Rate', align: 'right', render: (row) => formatPercent(row.clickRate) },
                        { key: 'ctor', label: 'CTOR', align: 'right', render: (row) => formatPercent(row.ctor) },
                    ]}
                />

                <EmailTableSection
                    title="Forms In Range"
                    subtitle="Secondary HubSpot context kept below the email reporting sections."
                    rows={formRows}
                    emptyMessage="No HubSpot form signups available for the selected period."
                    columns={[
                        { key: 'name', label: 'Form', render: (row) => row.name },
                        { key: 'views', label: 'Views', align: 'right', render: (row) => formatNumber(row.views) },
                        { key: 'submissions', label: 'Submissions', align: 'right', render: (row) => formatNumber(row.submissions) },
                        { key: 'conversionRate', label: 'Conversion', align: 'right', render: (row) => formatPercent(row.conversionRate) },
                    ]}
                />
            </Box>
        </Box>
    );
};

export default HubSpotReport;
